import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../Contexts/AuthenticateProvider";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../Helpers/Utils";
import BackButton from "../../Components/BackButton";
import Loading from "../../Components/Loading";
import Timeline from "../../Components/Timeline";
import BrandFormModal from "./BrandFormModal";
import ProfileAvatar from "../../Assets/images/avatar.png";

const DetailRow = ({ label, value, capitalize = false }) => (
    <div className="row align-items-sm-center">
        <label className="fw-bold col-sm-6 col-form-label">{label}</label>
        <div className={`col-sm-6 d-flex align-items-center ${capitalize ? "text-capitalize" : ""}`}>
            {value || value === 0 ? value : "-"}
        </div>
    </div>
);

const BrandDetail = () => {
    const { hasPermission } = useAuth();
    const canUpdate = hasPermission("vehicle.brand.update");
    const canStatus = hasPermission("vehicle.brand.status");
    const { brandId } = useParams();
    const [brand, setBrand] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingStatus, setTogglingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [brandResponse, logResponse] = await Promise.all([
            Handler({ method: "GET", url: `/staff/vehicle/brand/${brandId}` }),
            Handler({ method: "GET", url: `/staff/vehicle/brand/${brandId}/log`, params: { limit: 20 } }),
        ]);
        setBrand(brandResponse?.success ? brandResponse?.data?.data : null);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setLoading(false);
    }, [brandId]);

    useEffect(() => { load(); }, [load]);

    const toggleStatus = async () => {
        setTogglingStatus(true);
        const response = await Handler({ method: "patch", url: `/staff/vehicle/brand/${brandId}/status`, data: { status: !brand?.status } });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setTogglingStatus(false);
    };

    if (loading) {
        return (
            <Loading />
        );
    }

    if (!brand) {
        return (
            <div className="container-fluid">
                <BackButton name="to brands" url="/brand" className="mb-3" />
                <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-body text-center py-5">
                        <p className="text-muted mb-0">Brand not found.</p>
                    </div>
                </div>
            </div>
        );
    }

    const isActive = brand.status === true;

    return (
        <React.Fragment>
            <div className="container-fluid">
                <BackButton name="to brands" url="/brand" className="mb-3" />

                <div className="row g-4">
                    <div className="col-12 col-xl-8">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-2 justify-content-between flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <h3 className="text-primary text-capitalize mb-0">{brand.brand || ""}</h3>
                                        <span className={`badge rounded-pill ${isActive ? "text-bg-success" : "text-bg-danger"}`}>
                                            <i className={`bi ${isActive ? "bi-check-circle" : "bi-x-circle"}`}></i>
                                            {isActive ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                    <div className="text-end d-flex flex-wrap gap-2">
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={toggleStatus} disabled={togglingStatus || !canStatus} hidden={!canStatus}>
                                            {togglingStatus ? "Updating..." : isActive ? "Deactivate" : "Activate"}
                                        </button>
                                        {canUpdate ? <button type="button" className="btn btn-sm btn-primary" disabled={loading} onClick={() => setShowEditModal(true)}>Edit brand</button> : ""}
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <img
                                        src={brand.logo || ProfileAvatar}
                                        alt={brand.brand}
                                        className="rounded-circle"
                                        width={80}
                                        height={80}
                                        style={{ objectFit: "cover" }}
                                    />
                                </div>

                                <div className="row">
                                    <div className="col-xl-8">
                                        <DetailRow label="Created At" value={formatDateTime(brand.createdAt)} />
                                        <DetailRow label="Brand Id" value={brand.brandId} />
                                        <DetailRow label="Brand Name" value={brand.brand} capitalize />
                                        <DetailRow label="Type" value={brand.brandType} capitalize />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-4">
                        <Timeline userLog={logs} />
                    </div>
                </div>
            </div>

            {showEditModal ? (
                <BrandFormModal mode="edit" brand={brand} onClose={() => setShowEditModal(false)} onSaved={() => { setShowEditModal(false); load(); }} />
            ) : ""}
        </React.Fragment>
    );
};

export default BrandDetail;
